import {
  isConfidentFuzzyIntent,
  resolveAgentIntentFuzzy,
  type AgentIntentFuzzyContext,
} from './agentIntentFuzzy';
import type { AgentIntentResolution } from './agentIntentCatalog';

export type AgentProjectPickAction =
  | 'apply_ordering'
  | 'apply_default_headers'
  | 'apply_bearer'
  | 'suggest_datasets'
  | 'guide_run'
  | 'run_request_by_request'
  | 'run_round_robin'
  | 'run_full_flow'
  | 'run_collection'
  | 'test_cycles';

export type AgentFormVariant =
  | 'create_select'
  | 'import_swagger'
  | 'create_environment'
  | 'env_variable'
  /** @deprecated Redirected to apply_env_to_request; kept for legacy page-nav payloads. */
  | 'apply_env_variables'
  | 'apply_env_to_request'
  | 'execute_request'
  | 'copy_request_to_all_collections'
  | 'set_login_credentials'
  | 'create_function'
  | 'run_function';

export type AgentShortcut =
  | { kind: 'form'; variant: AgentFormVariant; mode?: 'new' | 'existing' }
  | { kind: 'project_pick'; action: AgentProjectPickAction }
  | { kind: 'action'; action: string; data?: Record<string, unknown> };

export type InterpretAgentShortcutOptions = AgentIntentFuzzyContext;

const FORM_VARIANTS = new Set<string>([
  'create_select',
  'import_swagger',
  'create_environment',
  'env_variable',
  'apply_env_variables',
  'apply_env_to_request',
  'execute_request',
  'copy_request_to_all_collections',
  'set_login_credentials',
  'create_function',
  'run_function',
]);

const PROJECT_PICK_ACTIONS = new Set<string>([
  'apply_ordering',
  'apply_default_headers',
  'apply_bearer',
  'suggest_datasets',
  'guide_run',
  'run_request_by_request',
  'run_round_robin',
  'run_full_flow',
  'run_collection',
  'test_cycles',
]);

/**
 * Actions that prefer conversational slot-filling when the catalog has no formVariant.
 * If a formVariant is declared (AI Agent structured UI), we still open that form.
 */
const MIGRATED_TO_CONVERSATION = new Set<string>([
  'execute_login',
  'run_single_request',
]);

/** Map a confident catalog intent resolution onto the existing shortcut union. */
export function shortcutFromIntentResolution(
  resolution: AgentIntentResolution,
): AgentShortcut | null {
  if (!isConfidentFuzzyIntent(resolution)) return null;

  // Structured forms win for the AI Agent page (Create env, Import Swagger, etc.).
  if (resolution.outcome === 'form' && resolution.formVariant && FORM_VARIANTS.has(resolution.formVariant)) {
    const variant = resolution.formVariant as AgentFormVariant;
    if (variant === 'create_select' && (resolution.mode === 'new' || resolution.mode === 'existing')) {
      return { kind: 'form', variant, mode: resolution.mode };
    }
    return { kind: 'form', variant };
  }

  if (resolution.backendAction && MIGRATED_TO_CONVERSATION.has(resolution.backendAction)) {
    return null;
  }

  if (resolution.outcome === 'project_pick' && resolution.backendAction) {
    const action =
      resolution.backendAction === 'apply_bearer'
        ? 'apply_bearer'
        : resolution.backendAction === 'apply_default_headers'
          ? 'apply_default_headers'
          : resolution.backendAction === 'suggest_datasets'
            ? 'suggest_datasets'
            : resolution.backendAction;
    if (PROJECT_PICK_ACTIONS.has(action)) {
      return { kind: 'project_pick', action: action as AgentProjectPickAction };
    }
  }

  if (resolution.outcome === 'navigate' && resolution.backendAction === 'run_collection') {
    return { kind: 'project_pick', action: 'run_collection' };
  }

  if (resolution.outcome === 'navigate' && resolution.backendAction === 'test_cycles') {
    return { kind: 'project_pick', action: 'test_cycles' };
  }

  if (resolution.backendAction && PROJECT_PICK_ACTIONS.has(resolution.backendAction)) {
    return { kind: 'project_pick', action: resolution.backendAction as AgentProjectPickAction };
  }

  return null;
}

// --- Phase 6: legacy fallbacks only where catalog phrases cannot express scope rules ---

function mentionsEnvVariablePhrase(has: (s: string) => boolean): boolean {
  return (
    has('environment variable') ||
    has('environment variables') ||
    has('env variable') ||
    has('env variables') ||
    has('env vars') ||
    has('env var')
  );
}

function mentionsApplyVariablePhrase(t: string, has: (s: string) => boolean): boolean {
  return (
    mentionsEnvVariablePhrase(has) ||
    has('variables') ||
    has(' variable') ||
    t === 'apply variable' ||
    t === 'apply variables' ||
    t === 'apply vars'
  );
}

function mentionsRequestBodyPhrase(has: (s: string) => boolean): boolean {
  return (
    has('request body') ||
    has('request bodies') ||
    has('request-body') ||
    has('request-bodies') ||
    has('matching request bodies') ||
    has('matching request body')
  );
}

function targetsAllRequestsScope(has: (s: string) => boolean): boolean {
  return (
    has('all requests') ||
    has('all request bodies') ||
    has('all request body') ||
    has('all request') ||
    has('every request') ||
    has('matching request bodies') ||
    has('matching request body') ||
    has('to all') ||
    (mentionsRequestBodyPhrase(has) && has('all') && !has('selected'))
  );
}

function targetsSelectedRequestScope(has: (s: string) => boolean): boolean {
  if (has('matching request bodies') || has('matching request body')) {
    return false;
  }
  return (
    has('selected request body') ||
    has('selected request') ||
    has('to request body') ||
    has('to request bodies') ||
    has('to request') ||
    has('this request') ||
    has('current request') ||
    (mentionsRequestBodyPhrase(has) && has('selected')) ||
    (has('request') &&
      !has('requests') &&
      !has('all request') &&
      !has('all requests') &&
      !has('all request bodies') &&
      !has('all request body'))
  );
}

function isApplyEnvToSelectedRequestIntent(t: string, has: (s: string) => boolean): boolean {
  if (!has('apply') || !mentionsApplyVariablePhrase(t, has)) {
    return false;
  }
  return (
    has('apply environment variables to selected request body') ||
    has('apply env variables to selected request body') ||
    has('apply environment variables to request body') ||
    has('apply env variables to request body') ||
    has('apply variables to selected request body') ||
    has('apply variables to request body') ||
    has('apply variable to selected request body') ||
    has('apply variable to request body') ||
    has('apply environment variables to selected request') ||
    has('apply env variables to selected request') ||
    has('apply environment variables to request') ||
    has('apply env variables to request') ||
    has('apply variables to selected request') ||
    has('apply variables to request') ||
    has('apply variable to selected request') ||
    has('apply variable to request') ||
    (has('selected request') && mentionsApplyVariablePhrase(t, has)) ||
    (mentionsRequestBodyPhrase(has) && has('selected') && mentionsApplyVariablePhrase(t, has)) ||
    (targetsSelectedRequestScope(has) &&
      mentionsApplyVariablePhrase(t, has) &&
      !targetsAllRequestsScope(has))
  );
}

function isApplyEnvToAllRequestsIntent(t: string, has: (s: string) => boolean): boolean {
  if (!has('apply') || has('add') || has('new environment variable')) {
    return false;
  }
  if (isApplyEnvToSelectedRequestIntent(t, has)) {
    return false;
  }
  return (
    has('apply environment variables to all request bodies') ||
    has('apply env variables to all request bodies') ||
    has('apply variables to all request bodies') ||
    has('apply environment variables to all request body') ||
    has('apply environment variables to matching request bodies') ||
    has('apply env variables to matching request bodies') ||
    has('apply environment variables to all requests') ||
    has('apply env variables to all requests') ||
    has('apply variables to all requests') ||
    has('apply variables to all request bodies') ||
    has('apply variables to all') ||
    (has('apply') && mentionsEnvVariablePhrase(has) && targetsAllRequestsScope(has)) ||
    (has('apply') && mentionsEnvVariablePhrase(has) && mentionsRequestBodyPhrase(has) && !has('selected')) ||
    (has('apply') && (has('environment') || has(' env ')) && has('variable') && has('all'))
  );
}

function isAddEnvVariableIntent(t: string, has: (s: string) => boolean): boolean {
  if (has('apply')) {
    return false;
  }
  return (
    has('add environment variable') ||
    has('add env variable') ||
    has('new environment variable') ||
    has('set environment variable') ||
    t === 'environment variable' ||
    t === 'env variable' ||
    (has('add') && has('variable') && (has('environment') || has(' env '))) ||
    (has('set') && has('variable') && (has('environment') || has(' env ')) && !has('login'))
  );
}

/**
 * Matches quick-intent prompts and typed shortcuts on the AI Agent page / Assistant.
 * Phase 6: shared catalog fuzzy is primary; legacy rules are limited to apply-env scope + bare "swagger".
 */
export function interpretAgentShortcut(
  text: string,
  options?: InterpretAgentShortcutOptions,
): AgentShortcut | null {
  const t = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t || t.length > 240) return null;

  const fuzzy = resolveAgentIntentFuzzy(text, options);
  const fromCatalog = fuzzy ? shortcutFromIntentResolution(fuzzy) : null;
  if (fromCatalog) return fromCatalog;

  const has = (s: string) => t.includes(s);

  if (isApplyEnvToSelectedRequestIntent(t, has) || isApplyEnvToAllRequestsIntent(t, has)) {
    return { kind: 'form', variant: 'apply_env_to_request' };
  }
  if (isAddEnvVariableIntent(t, has)) {
    return { kind: 'form', variant: 'env_variable' };
  }
  if (
    has('import swagger') ||
    has('import openapi') ||
    t === 'swagger' ||
    t === 'openapi' ||
    has('swagger url') ||
    has('openapi url')
  ) {
    return { kind: 'form', variant: 'import_swagger' };
  }
  if (
    has('diagnose proxy') ||
    has('proxy connection') ||
    has('proxy diagnose') ||
    t === 'diagnose proxy connection'
  ) {
    return { kind: 'action', action: 'diagnose_proxy' };
  }
  if (
    has('create environment') ||
    has('new environment') ||
    t === 'environment' ||
    (has('create') && has('environment') && !has('variable'))
  ) {
    return { kind: 'form', variant: 'create_environment' };
  }

  return null;
}
